import { useSettings } from "@/contexts/SettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Build a full URL for an uploaded asset path returned from /api/uploads/image.
 * Accepts either an absolute http(s) URL (returned as-is) or a relative path.
 */
export function resolveAssetUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/")) return `${BACKEND_URL}${pathOrUrl}`;
  return `${BACKEND_URL}/api/uploads/${pathOrUrl}`;
}

/**
 * Logo component — renders the uploaded brand logo if `settings.logo_url`
 * is set, otherwise falls back to the colored square + letter.
 *
 * Variants:
 *   - "dark"  → bone background, emerald letter (used on dark hero / footer)
 *   - "light" → emerald background, bone letter (used on white pages)
 */
export default function Logo({ size = 40, variant = "light", letter, className = "" }) {
  const s = useSettings();
  const logoUrl = resolveAssetUrl(s.logo_url);
  const ch = letter ?? s.logo_letter ?? "A";

  if (logoUrl) {
    return (
      <div
        className={`relative flex items-center justify-center overflow-hidden ${className}`}
        style={{ width: size, height: size }}
      >
        <img
          src={logoUrl}
          alt={`${s.company_name || "Company"} logo`}
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>
    );
  }

  const colors = variant === "dark"
    ? "bg-bone text-emerald"
    : "bg-emerald text-bone";

  return (
    <div
      className={`relative flex items-center justify-center transition-colors ${colors} ${className}`}
      style={{ width: size, height: size }}
    >
      <span className="font-serif leading-none" style={{ fontSize: size * 0.5 }}>{ch}</span>
      <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-gold" />
    </div>
  );
}
